import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import { useOnBrand } from '@/lib/on-brand';
import { cn } from '@/lib/utils';

/**
 * Button (DESIGN_SYSTEM §4): primary · secondary · ghost · destructive · link · accent (gold) ·
 * on-brand / on-brand-outline (actions that sit on the blue band). Sizes sm 32 / md 40 / lg 48 px;
 * below `md` every size is at least 40 px (touch targets, DESIGN_SYSTEM §8). Icon-only buttons
 * need an `aria-label` (and a tooltip where the icon is not obvious).
 */
const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-transparent text-body font-semibold whitespace-nowrap transition-colors duration-150 ease-out disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary: 'border-border-strong bg-surface text-foreground hover:bg-surface-muted',
        ghost: 'text-primary hover:bg-primary-soft',
        destructive: 'bg-danger text-danger-foreground hover:brightness-90',
        link: 'h-auto border-0 px-0 text-primary underline-offset-4 hover:underline',
        accent: 'bg-accent-gold text-on-accent hover:bg-accent-gold/85',
        // On the blue band the default focus colour has too little contrast: outline in white.
        'on-brand': 'bg-on-brand text-brand hover:bg-on-brand/90 focus-visible:outline-on-brand',
        'on-brand-outline':
          'border-on-brand/55 bg-transparent text-on-brand hover:bg-on-brand/8 focus-visible:outline-on-brand',
      },
      size: {
        sm: 'h-control-sm gap-1.5 px-3 text-small has-[>svg]:px-2.5 max-md:h-control-md',
        md: 'h-control-md px-4 has-[>svg]:px-3',
        lg: 'h-control-lg gap-2.5 px-6 text-section font-sans has-[>svg]:px-5',
        'icon-sm': 'size-control-sm max-md:size-control-md',
        icon: 'size-control-md',
        'icon-lg': 'size-control-lg',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0 has-[>svg]:px-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

/**
 * Inside the blue page band (T-098) the surface variants have too little contrast: the main
 * action turns white (`on-brand`), quieter ones outlined. Gold `accent` and `destructive` keep
 * their own labels and pass on the band as they are.
 */
const ON_BRAND: Partial<Record<ButtonVariant, ButtonVariant>> = {
  primary: 'on-brand',
  secondary: 'on-brand-outline',
  ghost: 'on-brand-outline',
  link: 'on-brand-outline',
};

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a spinner, keeps the button's width and blocks clicks. */
    loading?: boolean;
  };

function Button({
  className,
  variant = 'primary',
  size = 'md',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  const onBrand = useOnBrand();
  const look = onBrand && variant ? (ON_BRAND[variant] ?? variant) : variant;
  return (
    <Comp
      data-slot="button"
      data-variant={look}
      data-size={size}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      disabled={disabled ?? loading}
      className={cn(
        buttonVariants({ variant: look, size, className }),
        loading && 'pointer-events-none cursor-progress',
      )}
      {...props}
    >
      {loading && !asChild ? (
        <>
          {/* opacity, not visibility: the label keeps the width and stays the accessible name */}
          <span className="inline-flex items-center gap-[inherit] opacity-0">{children}</span>
          <Loader2 className="absolute animate-spin" aria-hidden="true" />
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants, type ButtonProps };
